import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ClassificationBadge } from "@/components/Badges";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Zap, AlertTriangle, ExternalLink } from "lucide-react";

export default function Intelligence() {
  const { user } = useAuth();
  const [status, setStatus] = useState({ serper_configured: false });
  const [feed, setFeed] = useState([]);
  const [classification, setClassification] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const canSweepAll = user?.role === "crm_manager";

  const load = async () => {
    setLoading(true);
    try {
      const [s, f] = await Promise.all([
        api.get("/intelligence/status"),
        api.get("/intelligence/feed", { params: { classification: classification || undefined, limit: 200 } }),
      ]);
      setStatus(s.data); setFeed(f.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [classification]);

  const sweepAll = async () => {
    setSweeping(true);
    try {
      const { data } = await api.post("/intelligence/sweep-all");
      toast.success(`Swept ${data.clients} clients · ${data.items} items · ${data.adverse} adverse`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSweeping(false); }
  };

  const filtered = feed.filter((r) => !q ||
    (r.client_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (r.headline || "").toLowerCase().includes(q.toLowerCase()));

  const counts = {
    total: feed.length,
    adverse: feed.filter((f) => f.classification === "adverse").length,
    positive: feed.filter((f) => f.classification === "positive").length,
    neutral: feed.filter((f) => f.classification === "neutral").length,
  };

  return (
    <div className="p-6 md:p-8 space-y-4" data-testid="intelligence-page">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <div className="mptg-overline">Signals</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">AI Market Intelligence</h1>
          <p className="text-slate-500 mt-1">
            {status.serper_configured
              ? "Live web sweep via Serper · Claude classification"
              : "Running in demo mode — add SERPER_API_KEY in backend/.env for live web search"}
          </p>
        </div>
        {canSweepAll && (
          <Button onClick={sweepAll} disabled={sweeping} className="bg-[#2E86C1] hover:bg-[#2471A3] text-white" data-testid="sweep-all-btn">
            <Zap className="w-4 h-4 mr-1" /> {sweeping ? "Sweeping…" : "Sweep all clients"}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Total items" value={counts.total} />
        <Card label="Adverse" value={counts.adverse} tone="danger" />
        <Card label="Positive" value={counts.positive} tone="success" />
        <Card label="Neutral" value={counts.neutral} />
      </div>

      <div className="mptg-card flex flex-wrap gap-3 items-center">
        <Input placeholder="Search client or headline…" value={q} onChange={(e) => setQ(e.target.value)} className="flex-1 min-w-[240px]" data-testid="intel-search" />
        <Select value={classification || "all"} onValueChange={(v) => setClassification(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="intel-classification-filter"><SelectValue placeholder="Classification" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All classifications</SelectItem>
            <SelectItem value="adverse">Adverse</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="mptg-card text-center text-slate-400 py-10">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="mptg-card text-center text-slate-400 py-10">
            No intelligence items yet. {canSweepAll && "Click Sweep all clients to generate."}
          </div>
        ) : (
          filtered.map((r) => (
            <div key={r.id} className="mptg-card mptg-card-hover" data-testid={`intel-item-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <ClassificationBadge classification={r.classification} />
                    <Link to={`/clients/${r.client_id}`} className="text-sm font-semibold text-[#2E86C1] hover:underline">
                      {r.client_name}
                    </Link>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-500">{r.source_name || "Source"}</span>
                    <span className="text-xs text-slate-400">·</span>
                    <span className="text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</span>
                  </div>
                  <h3 className="font-heading font-bold text-base mt-1.5 tracking-tight">{r.headline}</h3>
                  <p className="text-sm text-slate-600 mt-1 leading-relaxed">{r.summary}</p>
                </div>
                {r.source_url && (
                  <a
                    href={r.source_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-[#2E86C1] hover:underline whitespace-nowrap flex items-center gap-1"
                  >
                    Source <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function Card({ label, value, tone }) {
  const tones = { success: "text-[#1E8449]", warning: "text-[#F39C12]", danger: "text-[#C0392B]" };
  return (
    <div className="mptg-card">
      <div className="mptg-overline">{label}</div>
      <div className={`font-heading text-2xl font-bold mt-1 ${tones[tone] || "text-slate-800"}`}>{value}</div>
    </div>
  );
}
